import {
	AUTHORIZATION_PLATFORM_READ,
	AUTHORIZATION_PLATFORM_WRITE,
	AUTHORIZATION_ROLES_READ,
	AUTHORIZATION_ROLES_WRITE,
	listAthenaAuthorizationRights,
} from "../../runtime/authorization/catalog.ts";
import { requireExpectedVersion } from "../../runtime/authorization/role-invariants.ts";
import { missingRequiredRights } from "../../rights/matching.ts";
import {
	verifiedAuthorizationCommandScope,
} from "./authorization-guard.ts";
import { AthenaAuthRuntimeError, jsonResponse } from "./errors.ts";
import type { AthenaAuthStores } from "./memory-stores.ts";
import type { AuthSessionRow, AuthUserRow } from "./models.ts";
import { asStringField, readJsonBody, requireStringField } from "./security.ts";
import { resolveFoundingOwnerUserId } from "./organization-invariants.ts";

function asStringArray(body: Record<string, unknown>, key: string): string[] {
	const value = body[key];
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter((entry): entry is string => typeof entry === "string");
}

function roleIdFromPath(path: string): string | undefined {
	const match = /^\/authorization\/roles\/([^/]+)(?:\/rights)?$/.exec(path);
	const id = match?.[1];
	if (!id || id === "clone") {
		return undefined;
	}
	return id;
}

export async function handleAuthorizationRoute(
	request: Request,
	path: string,
	method: string,
	stores: AthenaAuthStores,
	headers: Headers,
	requireSession: (
		request: Request,
		stores: AthenaAuthStores,
	) => Promise<{ session: AuthSessionRow; user: AuthUserRow }>,
	bodyLimitBytes: number,
): Promise<Response | undefined> {
	if (path === "/authorization/snapshot" && method === "GET") {
		const resolved = await requireSession(request, stores);
		const snapshot = await buildSnapshot(stores, resolved);
		return jsonResponse(200, snapshot, headers);
	}

	if (path === "/authorization/rights" && method === "GET") {
		const resolved = await requireSession(request, stores);
		await requireRoleAccess(stores, resolved, request, "read");
		return jsonResponse(
			200,
			{ rights: listAthenaAuthorizationRights() },
			headers,
		);
	}

	if (path === "/authorization/roles" && method === "GET") {
		const resolved = await requireSession(request, stores);
		const command = await requireRoleAccess(stores, resolved, request, "read");
		const roles = await stores.authorization.listRoles({
			organizationId: command.scope.organizationId,
			scopeKind: command.scope.kind,
		});
		return jsonResponse(200, { roles }, headers);
	}

	if (path === "/authorization/roles" && method === "POST") {
		const resolved = await requireSession(request, stores);
		const body = await readJsonBody(request, bodyLimitBytes);
		const command = await requireRoleAccess(
			stores,
			resolved,
			request,
			"write",
			body,
		);
		const name = requireStringField(body, "name");
		const role = await stores.authorization.createRole({
			actorUserId: resolved.user.id,
			name,
			organizationId: command.scope.organizationId,
			rights: asStringArray(body, "rights"),
			scopeKind: command.scope.kind,
		});
		return jsonResponse(200, { role }, headers);
	}

	if (path === "/authorization/roles/clone" && method === "POST") {
		const resolved = await requireSession(request, stores);
		const body = await readJsonBody(request, bodyLimitBytes);
		const sourceRoleId = requireStringField(body, "sourceRoleId");
		const name = asStringField(body, "name") ?? "Cloned role";
		const command = await requireRoleAccess(
			stores,
			resolved,
			request,
			"write",
			body,
		);
		if (command.scope.kind === "organization") {
			const member = await stores.getMember(
				command.scope.organizationId ?? "",
				resolved.user.id,
			);
			if (!member) {
				throw AthenaAuthRuntimeError.forbidden();
			}
		}
		const cloned = await stores.authorization.cloneRole({
			actorUserId: resolved.user.id,
			name,
			organizationId: command.scope.organizationId,
			sourceRoleId,
		});
		return jsonResponse(200, { role: cloned }, headers);
	}

	const roleId = roleIdFromPath(path);
	if (
		method === "GET" &&
		(path === "/authorization/roles/{id}" ||
			(Boolean(roleId) && path === `/authorization/roles/${roleId}`))
	) {
		const resolved = await requireSession(request, stores);
		const command = await requireRoleAccess(stores, resolved, request, "read");
		const id = roleIdFromPath(path) ?? "";
		const role = await stores.authorization.getRole(
			id,
			command.scope.organizationId,
		);
		if (!role) {
			throw AthenaAuthRuntimeError.notFound("Role not found");
		}
		return jsonResponse(200, { role }, headers);
	}

	if (
		method === "PATCH" &&
		(path === "/authorization/roles/{id}" ||
			(Boolean(roleId) && path === `/authorization/roles/${roleId}`))
	) {
		const resolved = await requireSession(request, stores);
		const body = await readJsonBody(request, bodyLimitBytes);
		const command = await requireRoleAccess(
			stores,
			resolved,
			request,
			"write",
			body,
		);
		const id = roleIdFromPath(path) ?? "";
		const role = await stores.authorization.updateRole({
			actorUserId: resolved.user.id,
			expectedVersion: requireExpectedVersion(body.expectedVersion),
			id,
			name: requireStringField(body, "name"),
			organizationId: command.scope.organizationId,
		});
		return jsonResponse(200, { role }, headers);
	}

	if (
		method === "PUT" &&
		(path === "/authorization/roles/{id}/rights" ||
			(Boolean(roleId) && path === `/authorization/roles/${roleId}/rights`))
	) {
		const resolved = await requireSession(request, stores);
		const body = await readJsonBody(request, bodyLimitBytes);
		const command = await requireRoleAccess(
			stores,
			resolved,
			request,
			"write",
			body,
		);
		const id = roleIdFromPath(path) ?? "";
		const role = await stores.authorization.replaceRoleRights({
			actorUserId: resolved.user.id,
			expectedVersion: requireExpectedVersion(body.expectedVersion),
			id,
			organizationId: command.scope.organizationId,
			rights: asStringArray(body, "rights"),
		});
		return jsonResponse(200, { role }, headers);
	}

	if (
		method === "DELETE" &&
		(path === "/authorization/roles/{id}" ||
			(Boolean(roleId) && path === `/authorization/roles/${roleId}`))
	) {
		const resolved = await requireSession(request, stores);
		const url = new URL(request.url);
		let body: Record<string, unknown> = {};
		const contentType = request.headers.get("content-type") ?? "";
		if (contentType.includes("application/json")) {
			body = await readJsonBody(request, bodyLimitBytes);
		}
		const command = await requireRoleAccess(
			stores,
			resolved,
			request,
			"write",
			body,
		);
		const reassignmentRoleId =
			asStringField(body, "reassignmentRoleId") ??
			url.searchParams.get("reassignmentRoleId");
		await stores.authorization.deleteRole({
			actorUserId: resolved.user.id,
			expectedVersion: requireExpectedVersion(
				body.expectedVersion ?? url.searchParams.get("expectedVersion"),
			),
			id: roleIdFromPath(path) ?? "",
			organizationId: command.scope.organizationId,
			reassignmentRoleId,
		});
		return jsonResponse(200, { ok: true }, headers);
	}

	if (path === "/authorization/audit" && method === "GET") {
		const resolved = await requireSession(request, stores);
		const command = await requireRoleAccess(stores, resolved, request, "write");
		if (command.scope.kind === "organization") {
			const member = await stores.getMember(
				command.scope.organizationId ?? "",
				resolved.user.id,
			);
			if (!member) {
				throw AthenaAuthRuntimeError.forbidden();
			}
		}
		const entries = await stores.authorization.listAudit({
			organizationId: command.scope.organizationId,
			limit: 50,
		});
		return jsonResponse(200, { entries }, headers);
	}

	return undefined;
}

async function requireRoleAccess(
	stores: AthenaAuthStores,
	resolved: { session: AuthSessionRow; user: AuthUserRow },
	request: Request,
	access: "read" | "write",
	body?: Record<string, unknown>,
) {
	const snapshot = await buildSnapshot(stores, resolved);
	const rights = snapshot.effectiveRights;
	let orgRight: typeof AUTHORIZATION_ROLES_READ;
	let platformRight: typeof AUTHORIZATION_PLATFORM_READ;
	switch (access) {
		case "read":
			orgRight = AUTHORIZATION_ROLES_READ;
			platformRight = AUTHORIZATION_PLATFORM_READ;
			break;
		case "write":
			orgRight = AUTHORIZATION_ROLES_WRITE;
			platformRight = AUTHORIZATION_PLATFORM_WRITE;
			break;
		default: {
			const exhaustive: never = access;
			throw new Error(`unreachable authorization access: ${exhaustive}`);
		}
	}
	const orgOk = missingRequiredRights(rights, [orgRight]).length === 0;
	const platformOk =
		missingRequiredRights(rights, [platformRight]).length === 0;
	if (!orgOk && !platformOk) {
		throw AthenaAuthRuntimeError.forbidden();
	}
	const requestedOrganizationId =
		asStringField(body ?? {}, "organizationId") ??
		(new URL(request.url).searchParams.get("organizationId") ?? undefined);
	const scope = verifiedAuthorizationCommandScope({
		activeOrganizationId: resolved.session.active_organization_id,
		canManageOrganizationRoles: orgOk,
		canManagePlatformRoles: platformOk,
		requestedOrganizationId,
	});
	if (scope.kind === "organization") {
		const member = await stores.getMember(
			scope.organizationId ?? "",
			resolved.user.id,
		);
		if (!member) {
			throw AthenaAuthRuntimeError.forbidden();
		}
	}
	return { scope, snapshot };
}

export async function buildSnapshot(
	stores: AthenaAuthStores,
	resolved: { session: AuthSessionRow; user: AuthUserRow },
) {
	const organizationId = resolved.session.active_organization_id;
	const organization = organizationId
		? await stores.getOrganization(organizationId)
		: undefined;
	const members = organizationId
		? await stores.listMembers(organizationId)
		: [];
	return stores.authorization.readSnapshot({
		activeOrganizationId: organizationId,
		foundingOwnerUserId: resolveFoundingOwnerUserId(organization, members),
		getMember: (orgId, userId) => stores.getMember(orgId, userId),
		listMembers: (orgId) => stores.listMembers(orgId),
		userId: resolved.user.id,
	});
}
